import merge from 'deepmerge';

/**
 * IFL's `css` function is based nearly entirely on Theme UI's. At some point,
 * for reasons unknown, in the early stages of development, a decision was made
 * to copy and paste Theme UI's functionality instead of it being a direct
 * dependency.
 *
 * Aligns fairly closely with Theme UI `0.15.5`. Deviations from Theme UI's
 * source code will be denoted below. One caveat: the types and interfaces
 * defined in `css/src/types.ts` are original to IFLs' initial implementation.
 * Aligning these with the current state of Theme UI proved challenging.
 *
 * @see https://github.com/system-ui/theme-ui/releases/tag/v0.15.5
 */

/**
 * Allows for nested scales with shorthand values
 * @example
 * {
 *   colors: {
 *     primary: { __default: '#00f', light: '#33f' }
 *   }
 * }
 * css({ color: 'primary' }); // { color: '#00f' }
 * css({ color: 'primary.light' }) // { color: '#33f' }
 *
 * Deviations:
 * - Default key renamed from "__default" to "[base]"
 *
 * @see https://github.com/system-ui/theme-ui/blob/v0.15.5/packages/css/src/index.ts#L11-L22
 */
const DEFAULT_KEY = "[base]";
const hasDefault = x => {
  return typeof x === "object" && x !== null && DEFAULT_KEY in x;
};

/**
 * Extracts value under path from a deeply nested object.
 * Used for Themes, variants and Theme UI style objects.
 * Given a path to object with `__default` key, returns the value under that key.
 *
 * @param obj a theme, variant or style object
 * @param path path separated with dots (`.`)
 * @param fallback default value returned if get(obj, path) is not found
 */
function get(obj, path, fallback, p, undef) {
  const pathArray = path && typeof path === "string" ? path.split(".") : [path];
  for (p = 0; p < pathArray.length; p++) {
    obj = obj ? obj[pathArray[p]] : undef;
  }
  if (obj === undef) return fallback;
  return hasDefault(obj) ? obj[DEFAULT_KEY] : obj;
}
const aliases = {
  bg: "backgroundColor",
  m: "margin",
  mt: "marginTop",
  mr: "marginRight",
  mb: "marginBottom",
  ml: "marginLeft",
  mx: "marginX",
  my: "marginY",
  p: "padding",
  pt: "paddingTop",
  pr: "paddingRight",
  pb: "paddingBottom",
  pl: "paddingLeft",
  px: "paddingX",
  py: "paddingY"
};
const multiples = {
  marginX: ["marginLeft", "marginRight"],
  marginY: ["marginTop", "marginBottom"],
  paddingX: ["paddingLeft", "paddingRight"],
  paddingY: ["paddingTop", "paddingBottom"],
  scrollMarginX: ["scrollMarginLeft", "scrollMarginRight"],
  scrollMarginY: ["scrollMarginTop", "scrollMarginBottom"],
  scrollPaddingX: ["scrollPaddingLeft", "scrollPaddingRight"],
  scrollPaddingY: ["scrollPaddingTop", "scrollPaddingBottom"],
  size: ["width", "height"]
};
const scales = {
  color: "colors",
  background: "colors",
  backgroundColor: "colors",
  borderColor: "colors",
  caretColor: "colors",
  columnRuleColor: "colors",
  outlineColor: "colors",
  textDecorationColor: "colors",
  opacity: "opacities",
  transition: "transitions",
  margin: "space",
  marginTop: "space",
  marginRight: "space",
  marginBottom: "space",
  marginLeft: "space",
  marginX: "space",
  marginY: "space",
  marginBlock: "space",
  marginBlockEnd: "space",
  marginBlockStart: "space",
  marginInline: "space",
  marginInlineEnd: "space",
  marginInlineStart: "space",
  padding: "space",
  paddingTop: "space",
  paddingRight: "space",
  paddingBottom: "space",
  paddingLeft: "space",
  paddingX: "space",
  paddingY: "space",
  paddingBlock: "space",
  paddingBlockEnd: "space",
  paddingBlockStart: "space",
  paddingInline: "space",
  paddingInlineEnd: "space",
  paddingInlineStart: "space",
  scrollMargin: "space",
  scrollMarginTop: "space",
  scrollMarginRight: "space",
  scrollMarginBottom: "space",
  scrollMarginLeft: "space",
  scrollMarginX: "space",
  scrollMarginY: "space",
  scrollPadding: "space",
  scrollPaddingTop: "space",
  scrollPaddingRight: "space",
  scrollPaddingBottom: "space",
  scrollPaddingLeft: "space",
  scrollPaddingX: "space",
  scrollPaddingY: "space",
  inset: "space",
  insetBlock: "space",
  insetBlockEnd: "space",
  insetBlockStart: "space",
  insetInline: "space",
  insetInlineEnd: "space",
  insetInlineStart: "space",
  top: "space",
  right: "space",
  bottom: "space",
  left: "space",
  gridGap: "space",
  gridColumnGap: "space",
  gridRowGap: "space",
  gap: "space",
  columnGap: "space",
  rowGap: "space",
  fontFamily: "fonts",
  fontSize: "fontSizes",
  fontWeight: "fontWeights",
  lineHeight: "lineHeights",
  letterSpacing: "letterSpacings",
  border: "borders",
  borderTop: "borders",
  borderRight: "borders",
  borderBottom: "borders",
  borderLeft: "borders",
  borderWidth: "borderWidths",
  borderStyle: "borderStyles",
  borderRadius: "radii",
  borderTopRightRadius: "radii",
  borderTopLeftRadius: "radii",
  borderBottomRightRadius: "radii",
  borderBottomLeftRadius: "radii",
  borderTopWidth: "borderWidths",
  borderTopColor: "colors",
  borderTopStyle: "borderStyles",
  borderBottomWidth: "borderWidths",
  borderBottomColor: "colors",
  borderBottomStyle: "borderStyles",
  borderLeftWidth: "borderWidths",
  borderLeftColor: "colors",
  borderLeftStyle: "borderStyles",
  borderRightWidth: "borderWidths",
  borderRightColor: "colors",
  borderRightStyle: "borderStyles",
  borderBlock: "borders",
  borderBlockColor: "colors",
  borderBlockEnd: "borders",
  borderBlockEndColor: "colors",
  borderBlockEndStyle: "borderStyles",
  borderBlockEndWidth: "borderWidths",
  borderBlockStart: "borders",
  borderBlockStartColor: "colors",
  borderBlockStartStyle: "borderStyles",
  borderBlockStartWidth: "borderWidths",
  borderBlockStyle: "borderStyles",
  borderBlockWidth: "borderWidths",
  borderEndEndRadius: "radii",
  borderEndStartRadius: "radii",
  borderInline: "borders",
  borderInlineColor: "colors",
  borderInlineEnd: "borders",
  borderInlineEndColor: "colors",
  borderInlineEndStyle: "borderStyles",
  borderInlineEndWidth: "borderWidths",
  borderInlineStart: "borders",
  borderInlineStartColor: "colors",
  borderInlineStartStyle: "borderStyles",
  borderInlineStartWidth: "borderWidths",
  borderInlineStyle: "borderStyles",
  borderInlineWidth: "borderWidths",
  borderStartEndRadius: "radii",
  borderStartStartRadius: "radii",
  columnRuleWidth: "borderWidths",
  boxShadow: "shadows",
  textShadow: "shadows",
  zIndex: "zIndices",
  width: "sizes",
  minWidth: "sizes",
  maxWidth: "sizes",
  height: "sizes",
  minHeight: "sizes",
  maxHeight: "sizes",
  flexBasis: "sizes",
  size: "sizes",
  blockSize: "sizes",
  inlineSize: "sizes",
  maxBlockSize: "sizes",
  maxInlineSize: "sizes",
  minBlockSize: "sizes",
  minInlineSize: "sizes",
  columnWidth: "sizes",
  backgroundImage: "colors",
  // svg
  fill: "colors",
  stroke: "colors"
};
const positiveOrNegative = (scale, value) => {
  if (typeof value !== "number" || value >= 0) {
    if (typeof value === "string" && value.startsWith("-")) {
      const valueWithoutMinus = value.substring(1);
      const n = get(scale, valueWithoutMinus, valueWithoutMinus);
      if (typeof n === "number") {
        return n * -1;
      }
      return `-${n}`;
    }
    return get(scale, value, value);
  }
  const absolute = Math.abs(value);
  const n = get(scale, absolute, absolute);
  // eslint-disable-next-line prefer-template
  if (typeof n === "string") return "-" + n;
  return Number(n) * -1;
};
const transforms = ["margin", "marginTop", "marginRight", "marginBottom", "marginLeft", "marginX", "marginY", "top", "bottom", "left", "right",
// Logical properties
"marginBlock", "marginBlockEnd", "marginBlockStart", "marginInline", "marginInlineEnd", "marginInlineStart", "insetBlock", "insetBlockEnd", "insetBlockStart", "insetInline", "insetInlineEnd", "insetInlineStart"].reduce((acc, curr) => ({
  ...acc,
  [curr]: positiveOrNegative
}), {});

/**
 * Deviations:
 * - argument types
 * - no use of `defaultBreakpoints
 *
 * @see https://github.com/system-ui/theme-ui/blob/v0.15.5/packages/css/src/index.ts#L306-L345
 */
const responsive = styles => theme => {
  const next = {};
  const breakpoints = get(theme, "breakpoints", []);
  const mediaQueries = [null, ...breakpoints.map(n => n.includes("@media") ? n : `@media screen and (min-width: ${n})`)];

  // eslint-disable-next-line guard-for-in
  for (const k in styles) {
    const key = k;
    const value = typeof styles[key] === "function" ? styles[key](theme || {}) : styles[key];
    if (value === false || value == null) {
      continue;
    }
    if (!Array.isArray(value)) {
      next[key] = value;
      continue;
    }
    for (let i = 0; i < value.slice(0, mediaQueries.length).length; i++) {
      const media = mediaQueries[i];
      if (!media) {
        next[key] = value[i];
        continue;
      }
      next[media] = next[media] || {};
      if (value[i] == null) continue;
      next[media][key] = value[i];
    }
  }
  return next;
};

/**
 * Deviations:
 * - argument types
 * - theme type
 * - does not call `getObjectWithVariants`
 *
 * @see https://github.com/system-ui/theme-ui/blob/v0.15.5/packages/css/src/index.ts#L349-L402
 */
const _css = args => function () {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const theme = {
    // ...defaultTheme,
    ...("theme" in props ? props.theme : props)
  };
  const objOrArr = typeof args === "function" ? args(theme) : args;

  /**
   * Enables array of objects. For example:
   * ```
   * sx={[{...}, {...}]}
   * ```
   *
   * Unique to IFL. Unsure if this was a deliberate decision by us to
   * support. Theme UI does not seem to support this natively.
   */
  const obj = Array.isArray(objOrArr) ? merge.all(objOrArr.filter(i => i && typeof i === "object")) : objOrArr;
  const styles = responsive(obj || {})(theme);
  let result = {};
  // eslint-disable-next-line guard-for-in
  for (const key in styles) {
    const x = styles[key];
    const val = typeof x === "function" ? x(theme) : x;

    /**
     * IFL legacy approach, as opposed to newish Theme UI
     * `getObjectWithVariants`.
     */
    if (key === "variant") {
      const variant = _css(get(theme, val))(theme);
      result = {
        ...result,
        ...variant
      };
      continue;
    }
    if (val && typeof val === "object") {
      if (hasDefault(val)) {
        result[key] = val[DEFAULT_KEY];
        continue;
      }

      // On type level, val can also be an array here,
      // but we transform all arrays in `responsive` function.
      result[key] = _css(val)(theme);
      continue;
    }
    const prop = key in aliases ? aliases[key] : key;
    const scaleName = prop in scales ? scales[prop] : undefined;
    const scale = scaleName ? theme?.[scaleName] : get(theme, prop, {});
    const transform = get(transforms, prop, get);
    const value = transform(scale, val, val);
    if (prop in multiples) {
      const dirs = multiples[prop];
      for (let i = 0; i < dirs.length; i++) {
        result[dirs[i]] = value;
      }
    } else {
      result[prop] = value;
    }
  }
  return result;
};

/**
 * Transforms `input` to a style object that can be processed by CSS-in-JS
 * libraries. Note that this function should be used with the [css prop](https://emotion.sh/docs/css-prop).
 *
 * If you're using variants in your theme, you can access them by using the `variant`
 * property. The value of the property has to correspond to a path of your `Theme`.
 */
const css = args => function () {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return _css(args)(props);
};
const sxProp = props => css(props.sx)(props.theme);

export { css, css as default, get, sxProp };
